import {
  getCachedOdspDriverManifest,
  queueBestEffortToUpdateOdspDriverManifestOnIdle,
  fetchOdspDriverManifest,
  getBundleUrlFromManifest,
  isOdspDriverCachingMechanismEnabled
} from '@fluidx/odsp-driver-codeloader';
import { DriverType } from '../../DriverInfo/DriverInfo';
import { getRingFromAudience } from '@fluidx/utilities';
import { InitializeConfig } from '../../Iframe-shared/InitializeConfig';
import { SourcelessIframeLoadOptions } from '../../loadComponentInSourcelessIframe';
import { HostConfig } from '../../OfficeWebHostTypes';

export type AdditionalDriverProcessingResult = {
  scriptUrlsToPrepend?: string[];
  proceedWithDriverLoad?: Promise<void>;
};

// Within a given running session, no need to try to queue manifest refresh attempts
// more often than every 10 minutes
const MinimumTimeBetweenManifestCacheRefreshAttempts = 10 * 60 * 1000;
let lastManifestRequeueAttemptTimestamp: number = 0;

export function prepareAdditionalDriverProcessing(
  initializeConfig: InitializeConfig,
  hostConfig: HostConfig,
  options: SourcelessIframeLoadOptions
): AdditionalDriverProcessingResult {
  if (options.useServiceDeliveredDriver && getDriverType(initializeConfig) === DriverType.SPO) {
    const environment = hostConfig.environment;
    const deploymentRing = getRingFromAudience(environment?.audience);

    const cachedManifest = getCachedOdspDriverManifest(deploymentRing);
    if (cachedManifest) {
      const now = Date.now();
      if (now - lastManifestRequeueAttemptTimestamp > MinimumTimeBetweenManifestCacheRefreshAttempts) {
        lastManifestRequeueAttemptTimestamp = now;
        queueBestEffortToUpdateOdspDriverManifestOnIdle(deploymentRing);
      }

      return {
        scriptUrlsToPrepend: [getBundleUrlFromManifest(cachedManifest)]
      };
    }

    // Otherwise, queue up the manifest to be fetched, and provide a callback for the iframe-inside
    // to know that it can proceed with loading from the inside (once the manifest gets cached).
    // But note that it's only relevant if caching mechanism is enabled -- otherwise no point to wait on it
    // if the inside will need to re-fetch anyway.
    if (!isOdspDriverCachingMechanismEnabled()) {
      return {};
    }

    return {
      proceedWithDriverLoad: fetchOdspDriverManifest(deploymentRing).then(() => {})
    };
  }

  return {};
}

function getDriverType(initializeConfig: InitializeConfig): DriverType {
  switch (initializeConfig.type) {
    case 'Load':
      return initializeConfig.driverInfo.type;
    case 'CreateFromCode':
    case 'RehydrateFromSnapshot':
      return initializeConfig.driverType;
    // Note: no need for "default", since TS protects us from any other type falling through
  }
}
